import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { History, Monitor, Smartphone, ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

function parseUA(ua = "") {
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  const browser =
    /Firefox\//.test(ua) ? "Firefox" :
    /Edg\//.test(ua) ? "Edge" :
    /Chrome\//.test(ua) ? "Chrome" :
    /Safari\//.test(ua) ? "Safari" :
    /curl\//i.test(ua) ? "curl (script)" :
    "Autre";
  const os =
    /Windows/.test(ua) ? "Windows" :
    /Macintosh|Mac OS/.test(ua) ? "macOS" :
    /Android/.test(ua) ? "Android" :
    /iPhone|iPad|iPod/.test(ua) ? "iOS" :
    /Linux/.test(ua) ? "Linux" :
    "";
  return { browser, os, isMobile };
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const METHOD_LABEL = {
  password: "Email + mot de passe",
  google: "Google",
  "2fa": "Email + 2FA",
  session_refresh: "Reprise de session",
};

export default function LoginHistorySection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/security/login-history?limit=30");
        if (!cancelled) setItems(data || []);
      } catch { /* silent */ } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = expanded ? items : items.slice(0, 5);

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-6"
      data-testid="login-history-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
          <History size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900">Journal des connexions</p>
          <p className="text-sm text-slate-500">
            Les 30 dernières connexions à ton compte. Si tu ne reconnais pas une session, change ton mot de passe
            immédiatement.
          </p>
        </div>
      </div>

      {loading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-400">
          <Loader2 size={14} className="animate-spin" /> Chargement…
        </div>
      )}

      {!loading && items.length === 0 && (
        <p className="mt-4 text-sm text-slate-500 italic">
          Aucune connexion enregistrée. L'historique commence à partir d'aujourd'hui.
        </p>
      )}

      {!loading && items.length > 0 && (
        <>
          <ul className="mt-5 divide-y divide-slate-100" data-testid="login-history-list">
            {visible.map((it) => {
              const { browser, os, isMobile } = parseUA(it.user_agent || "");
              const Icon = isMobile ? Smartphone : Monitor;
              return (
                <li key={it.id} className="py-3 flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${it.success ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                    {it.success ? <ShieldCheck size={14} /> : <Icon size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <p className="text-sm font-medium text-slate-900">
                        {browser}{os ? ` · ${os}` : ""}
                      </p>
                      <span className="text-xs text-slate-400">
                        {METHOD_LABEL[it.method] || it.method}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatDate(it.created_at)}
                      {it.ip ? ` · IP ${it.ip}` : ""}
                    </p>
                    {!it.success && it.reason && (
                      <p className="text-xs text-rose-600 mt-1">Échec : {it.reason}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {items.length > 5 && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpanded((v) => !v)}
                data-testid="login-history-toggle-btn"
              >
                {expanded ? "Voir moins" : `Voir les ${items.length} connexions`}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
